// Адреса «Контроля» с условиями и листа контроля (приёмка 0.3.1). Условия «Контроля» живут
// в адресе страницы (T041): /control?view=open|aiClosed&asOf=sheet|today&group=overdue,burning
// &documentId=…&dueBefore=…&q=…&sort=source|due&responsibleId=…. Ссылки «Главной», «Документов»
// и палитры строятся здесь — тем же сериализатором, что у маршрутизатора: поиск «12» остаётся
// строкой.
//
// 005: адреса «Справочника» (экран — дорожка W1, маршрут /directory). Условия — поля RecordsQuery
// (kind, q, state, side, addressee, email, openItems, noPrimaryCurator, organizationId, documentId,
// newSinceUpload, withPairs, sort) и record (карточка записи справа), pair (окно сравнения пары) —
// договорённость дорожек W1 и W2. Признаки — true без кавычек: /directory?withPairs=true.
// 005-2: /directory?view=questions&doubt=kind — «Вопросы» только о виде записи.
// 030 (T020; FR-034): «Мои задачи» с группой срока — /my?group=overdue (экран — дорожка F).
import { CONTROL_SEARCH_MAX, DIRECTORY_SEARCH_MAX } from '@kp/contracts';
import type {
  AsOfMode,
  ControlGroupFilter,
  ControlSort,
  MyItemGroup,
  OrganizationSide,
  RecordKind,
  RecordRef,
  RecordState,
} from '@kp/contracts';
import { defaultStringifySearch, useNavigate } from '@tanstack/react-router';
import { useCallback } from 'react';
import type { AnchorHTMLAttributes, MouseEvent } from 'react';
import { routeUrl } from '../api/client.ts';
import { MY_ITEMS_PATH } from './permissions.ts';

/** «/my?group=overdue» — «Мои задачи» с группой срока; без группы — весь список. */
export function myItemsHref(group?: MyItemGroup): string {
  return group ? `${MY_ITEMS_PATH}${defaultStringifySearch({ group })}` : MY_ITEMS_PATH;
}

export interface ControlLink {
  view?: 'open' | 'aiClosed';
  asOf?: AsOfMode;
  /** Группы листа: «Просрочено», «Горит», «Неясно», «Проверить (ИИ)», без цвета. */
  group?: readonly ControlGroupFilter[];
  documentId?: string;
  dueBefore?: string;
  q?: string;
  sort?: ControlSort;
  /** Пункты, где упомянута запись справочника — фильтр «Ответственный» (005, FR-011). */
  responsibleId?: string;
}

/** «/control?asOf=today&group=overdue» — условия по порядку панели «Контроля». */
export function controlHref(link: ControlLink = {}): string {
  const search: Record<string, string> = {};
  if (link.view) search.view = link.view;
  if (link.asOf) search.asOf = link.asOf;
  if (link.group && link.group.length > 0) search.group = link.group.join(',');
  if (link.responsibleId) search.responsibleId = link.responsibleId;
  if (link.documentId) search.documentId = link.documentId;
  if (link.dueBefore) search.dueBefore = link.dueBefore;
  // Поиск сервера — не длиннее CONTROL_SEARCH_MAX знаков.
  const q = link.q?.trim().slice(0, CONTROL_SEARCH_MAX).trim();
  if (q) search.q = q;
  if (link.sort) search.sort = link.sort;
  return `/control${defaultStringifySearch(search)}`;
}

/** Условия «Справочника» в адресе (RecordsQuery) и открытые слои: карточка записи, пара. */
export interface DirectoryLink {
  /** Экран «Вопросы» по упоминаниям (T046). */
  view?: 'questions';
  /** На экране «Вопросы» — только «Похоже на другой вид» (005-2, FR-018). */
  doubt?: 'kind';
  kind?: RecordKind;
  q?: string;
  state?: readonly RecordState[];
  side?: OrganizationSide;
  addressee?: 'ready' | 'missing';
  email?: 'missing' | 'unconfirmed';
  openItems?: boolean;
  noPrimaryCurator?: boolean;
  organizationId?: string;
  documentId?: string;
  /** «Новое из последней загрузки» (FR-034а, FR-034б). */
  newSinceUpload?: boolean;
  /** Записи с парами «тот же?» без ответа. */
  withPairs?: boolean;
  /** Карточка записи боковой панелью справа. */
  record?: string;
  /** Окно сравнения пары «тот же?». */
  pair?: string;
}

/**
 * «/directory?kind=person&record=…», «/directory?addressee=missing», «/directory?view=questions» —
 * экран, условия в порядке полей RecordsQuery, затем record и pair.
 */
export function directoryHref(link: DirectoryLink = {}): string {
  const search: Record<string, string | boolean> = {};
  if (link.view) search.view = link.view;
  if (link.view && link.doubt) search.doubt = link.doubt;
  if (link.kind) search.kind = link.kind;
  const q = link.q?.trim().slice(0, DIRECTORY_SEARCH_MAX).trim();
  if (q) search.q = q;
  if (link.state && link.state.length > 0) search.state = link.state.join(',');
  if (link.side) search.side = link.side;
  if (link.addressee) search.addressee = link.addressee;
  if (link.email) search.email = link.email;
  if (link.openItems) search.openItems = true;
  if (link.noPrimaryCurator) search.noPrimaryCurator = true;
  if (link.organizationId) search.organizationId = link.organizationId;
  if (link.documentId) search.documentId = link.documentId;
  if (link.newSinceUpload) search.newSinceUpload = true;
  if (link.withPairs) search.withPairs = true;
  if (link.record) search.record = link.record;
  if (link.pair) search.pair = link.pair;
  return `/directory${defaultStringifySearch(search)}`;
}

/** Карточка записи справочника: «/directory?kind=person&record=…» (ссылка без ↗ — внутренняя). */
export function recordHref(record: Pick<RecordRef, 'id' | 'kind'>): string {
  return directoryHref({ kind: record.kind, record: record.id });
}

/** Лист контроля для печати: на дату листа (по умолчанию) или на сегодня (FR-017). */
export function controlSheetHref(documentId: string, asOf: AsOfMode = 'sheet'): string {
  return routeUrl('documents.controlSheet', {
    params: { id: documentId },
    ...(asOf === 'today' ? { query: { asOf } } : {}),
  });
}

/** Лист контроля — в новой вкладке, как ссылка с target="_blank" rel="noopener". */
export function openControlSheet(documentId: string, asOf: AsOfMode): void {
  window.open(controlSheetHref(documentId, asOf), '_blank', 'noopener');
}

/** Переход внутри приложения по готовому адресу с условиями. */
export function useGoHref(): (href: string) => void {
  const navigate = useNavigate();
  return useCallback((href: string) => void navigate({ href }), [navigate]);
}

/** Обычный щелчок левой кнопкой: колесо, ⌘, Ctrl и Shift оставляют ссылку браузеру. */
function isPlainClick(event: MouseEvent<HTMLAnchorElement>): boolean {
  return (
    !event.defaultPrevented &&
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export interface HrefLinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  href: string;
}

/**
 * Ссылка внутри приложения по готовому адресу с условиями: переход без перезагрузки страницы,
 * колесо и ⌘-щелчок открывают новую вкладку.
 */
export function HrefLink({ href, onClick, ...rest }: HrefLinkProps) {
  const go = useGoHref();
  return (
    <a
      href={href}
      {...rest}
      onClick={(event) => {
        onClick?.(event);
        if (!isPlainClick(event)) return;
        event.preventDefault();
        go(href);
      }}
    />
  );
}
